import { useEffect, useState } from 'react'
import { GraphResponse, GraphResponseData, PriceDropData } from '@/types'
import config from 'isomorphic-config'
import analytics from '@/shared-utils/analytics'
import useBootstrapData from './useBootstrapData'
import { jsonContentTypeHeader } from '../server/constants'
import useNearbyAirports from './useNearbyAirports'

type PriceDropError = {
  priceDropError: string
}

type PriceDropResult = {
  priceDrops: PriceDropData[]
}

type FlyPriceDropQueryResult = {
  flyPriceDrops: PriceDropError | PriceDropResult
}

function roundPriceDropData(data: PriceDropData[]) {
  return data
    .filter(priceDropDataRaw => Number(priceDropDataRaw.dealPrice)) // prevent NaN from showing up in UI
    .map(priceDropData => {
      const dealPriceRounded = Math.round(Number(priceDropData.dealPrice))
      return {
        ...priceDropData,
        dealPrice: dealPriceRounded.toString()
      }
    })
}

function isValidGraphQLResponse(
  payload: unknown
): payload is GraphResponse<FlyPriceDropQueryResult> {
  if (payload && payload instanceof Object) {
    if ('errors' in payload && Array.isArray(payload.errors)) {
      return true
    }
    if (
      'data' in payload &&
      payload.data &&
      typeof payload.data === 'object' &&
      'flyPriceDrops' in payload.data
    ) {
      const { flyPriceDrops } = payload.data
      if (
        flyPriceDrops &&
        typeof flyPriceDrops === 'object' &&
        ('priceDropError' in flyPriceDrops || 'priceDrops' in flyPriceDrops)
      ) {
        return true
      }
    }
  }
  return false
}

function isPriceDropQueryResult(
  payload: GraphResponse<FlyPriceDropQueryResult>
): payload is GraphResponseData<FlyPriceDropQueryResult> {
  if (
    'data' in payload &&
    typeof payload.data === 'object' &&
    payload.data &&
    'flyPriceDrops' in payload.data &&
    typeof payload.data.flyPriceDrops === 'object' &&
    payload.data.flyPriceDrops &&
    (('priceDropError' in payload.data.flyPriceDrops &&
      typeof payload.data.flyPriceDrops.priceDropError === 'string') ||
      ('priceDrops' in payload.data.flyPriceDrops &&
        Array.isArray(payload.data.flyPriceDrops.priceDrops)))
  ) {
    return true
  }
  return false
}

function isPriceDropResult(
  payload: PriceDropResult | PriceDropError
): payload is PriceDropResult {
  if (payload && 'priceDrops' in payload && Array.isArray(payload.priceDrops)) {
    return true
  }
  return false
}
type FetchPriceDropDataStatus = 'PENDING' | 'SUCCESS' | 'FAILED'

const useFetchPriceDropData = () => {
  const [priceDropData, setPriceDropData] = useState<PriceDropData[]>([])
  const [progress, setProgress] = useState<FetchPriceDropDataStatus>('PENDING')
  const { appName, appVersion } = useBootstrapData()
  const { nearbyAirportCodes, progress: nearbyAirportCodesProgress } =
    useNearbyAirports()

  useEffect(() => {
    let startTime: number
    async function fetchPriceDrop() {
      try {
        const { url, timeout } = config.client['pcln-graph']
        const requestUrl = `${url}?gqlOp=flyPriceDrops`
        const options = {
          method: 'POST',
          signal: AbortSignal.timeout(timeout),
          headers: {
            ...jsonContentTypeHeader,
            'apollographql-client-name': appName,
            'apollographql-client-version': appVersion
          },
          body: JSON.stringify({
            query: `
              query flyPriceDrops($priceDropRequestOptions: PriceDropRequestOptions!) {

                flyPriceDrops(priceDropRequestOptions: $priceDropRequestOptions) {
                ...on PriceDropResult {
                    priceDrops {
                        originCity
                        originAirportCode
                        originAirportCountryName
                        originAirportStateCode
                        destinationCity
                        destinationDmaName
                        destinationAirportCode
                        destinationAirportCountryName
                        destinationAirportStateCode
                        destinationImage
                        departDate
                        returnDate
                        strikePrice
                        dealPrice
                        percentageDrop
                        outboundAirlines
                        inboundAirlines
                        landingPageUrl
                        landingPageSlingshotUrl
                        promotionalCopy
                        alertDate
                        expiryDate
                    }
                }
                ...on PriceDropErr {
                    priceDropError
                }
              }
            }`,
            variables: {
              priceDropRequestOptions: {
                offset: 0,
                productType: 'FLY',
                flyPriceDropRequestOptions: {
                  destinationAirportCodes: [],
                  originAirportCodes: nearbyAirportCodes
                },
                resultSize: 12
              }
            }
          })
        }
        startTime = performance.now()
        const response = await fetch(requestUrl, options)
        const { status, statusText, type, url: urlValue } = response
        if (!response.ok) {
          const logObject = {
            response: {
              status,
              statusText,
              type,
              url: urlValue
            }
          }
          analytics.logError({
            message: `The price drop api response status is > 299. ${JSON.stringify(
              logObject
            )}`
          })
        } else {
          const responseData = await response.json()

          if (isValidGraphQLResponse(responseData)) {
            if (isPriceDropQueryResult(responseData)) {
              const { flyPriceDrops } = responseData.data
              if (isPriceDropResult(flyPriceDrops)) {
                const flyPriceDropsRounded = roundPriceDropData(
                  flyPriceDrops.priceDrops
                )
                setPriceDropData(flyPriceDropsRounded)
                setProgress('SUCCESS')
                analytics.log({
                  message: `Fly Price Drops fetch completed successfully`
                })
              } else {
                analytics.logError({
                  message: `${flyPriceDrops.priceDropError} ${JSON.stringify({
                    priceDropOption: options
                  })}`
                })
                setProgress('FAILED')
              }
            } else {
              analytics.logError({
                message: `The price drop graphql response returned errors.${JSON.stringify(
                  { errors: responseData.errors, priceDropOption: options }
                )}`
              })
              setProgress('FAILED')
            }
          } else {
            analytics.logError({
              message: `The price drop graphql response is not as per contract. ${JSON.stringify(
                { responseData, priceDropOption: options }
              )}`
            })
            setProgress('FAILED')
          }
        }
      } catch (error) {
        if ((error as Error).name === 'TimeoutError') {
          const elapsedTime = performance.now() - startTime
          analytics.logError({
            message: `Timeout reached while invoking fly price drop graph query. ${JSON.stringify(
              { elapsedTime, error }
            )}`
          })
        } else {
          analytics.logError({
            message: `Failed to query for Price Drop data. ${JSON.stringify({
              error
            })}`
          })
        }
        setProgress('FAILED')
      }
    }

    if (nearbyAirportCodesProgress === 'COMPLETE') {
      void fetchPriceDrop()
    }
  }, [appName, appVersion, nearbyAirportCodes, nearbyAirportCodesProgress])

  return { priceDropData, progress }
}

export default useFetchPriceDropData
